<template>
  <div class="display-in-center landing-view-layout view" v-loading.fullscreen="loading" id="Home_Loading">
    <div class="app-logo">
      <img alt="SmartBarcode logo" :src="image" class="home-logo" />
      <div v-if="isAuth">{{ $t('account.staff_name') }}: {{ currentUserName }}</div>
    </div>
    <CompanyName v-if="!isQuickTrackingMode" id="Home_CompanyName" />
    <div v-if="isAuth && projectDetail">
      <div class="help-block-container">
        <el-button
          id="Home_OnClickGenerate"
          type="primary"
          class="full-button"
          @click="onClickGenerate"
          :disabled="!allowGenerateNewBarcode"
        >
          <div class="action-button">
            <IconQRcode />
            {{ $t('Generate new barcode') }}
          </div>
        </el-button>
        <div v-if="!allowGenerateNewBarcode" class="help-block-container__child">
          <el-popover
            placement="top-start"
            :width="200"
            trigger="hover"
            :content="$t('require_permission_to_generate_barcode')"
          >
            <template #reference>
              <IconHelp />
            </template>
          </el-popover>
        </div>
      </div>
      <KeyenceScanButton v-if="isKeyenceEnterpriseBrowser" v-model:loading="loading" />
      <KeyenceScannerConfig v-if="isKeyenceEnterpriseBrowser" :scannerConfigName="'SingleScan'" />
      <router-link v-else :to="{ name: 'scan' }">
        <div class="help-block-container">
          <el-button id="Home_OnClickBarcodeScan" type="primary" class="full-button">
            <div class="action-button">
              <IconScanQR />
              {{ $t('Scan barcode') }}
            </div>
          </el-button>
        </div>
      </router-link>
      <SocketMobileScanComponent v-model:loading="loading" :on-scan="onScan" />
      <RfidScanButton v-if="isBizBrowserEnabled" mode="single-select" @rfid:confirmed="onRfidScanConfirmed" />
      <div v-if="!isQuickTrackingMode" class="mt-11"></div>
      <div class="help-block-container">
        <BarcodeSearchButton v-if="showBarcodeSearchPage" class="mt-3" />
        <DigitCountButton v-if="!isClientUser && showTrackingCountPage" class="mt-3" />
      </div>
    </div>

    <div v-else>
      <div id="Home_LoginForm" class="login-form">
        <label>{{ $t('Access Code') }}</label>
        <div id="Home_AccessCode" class="access-code">
          <el-input
            id="Home_FirstCode"
            v-model="firstCode"
            class="access-code-input"
            v-maska="'####'"
            type="tel"
            @input="onCodeInput"
          />
          -
          <el-input
            id="Home_LastCode"
            v-model="lastCode"
            class="access-code-input"
            v-maska="'####'"
            type="tel"
            ref="code"
            @keyup.enter="start"
          />
        </div>

        <div class="password-input field-input" v-if="isRequirePassword">
          <label>{{ $t('account.password') }}</label>
          <el-input id="Home_Password" v-model="password" type="password" />
        </div>

        <div class="password-input field-input" v-if="isRequireOTP">
          <label>{{ $t('account.confirm_code') }}</label>
          <el-input :disabled="isDisableCodeInput" id="Home_OTP" v-model="otpCode" />
        </div>

        <p v-if="error" class="error-msg">{{ error }}</p>
        <el-button id="Home_Start" type="primary" @click="start" circle class="go-button is-larger">
          {{ $t('Authenticate') }}
        </el-button>
      </div>
      <div class="app-links">
        <router-link
          id="Home_Register"
          v-if="isAllowUserSelfRegister"
          :to="{ name: 'register', params: { projectId: projectParam } }"
        >
          Register
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BarcodeSearchButton from '@/components/common/BarcodeSearchButton.vue'
import DigitCountButton from '@/components/common/DigitCountButton.vue'
import CompanyName from '@/components/CompanyName.vue'
import KeyenceScanButton from '@/components/keyenceHT/ScanButton.vue'
import KeyenceScannerConfig from '@/components/keyenceHT/ScannerConfig.vue'
import RfidScanButton from '@/components/bizBrowser/RfidScanButton.vue'
import SocketMobileScanComponent from '@/components/socketMobile/ScanComponent.vue'
import IconHelp from '@/components/svg/IconHelp.vue'
import IconLogout from '@/components/svg/IconLogout.vue'
import IconQRcode from '@/components/svg/IconQRcode.vue'
import IconScanQR from '@/components/svg/IconScanQR.vue'
import { CLEAR_BARCODE, FETCH_PROFILE, FETCH_PROJECT } from '@/store/actions'
import { LOCAL_STORAGE_ITEM, QRCODE_BARCODE_TYPE_REGEX } from '@/utils/constants'
import { generateBarcode, login } from '@/utils/api'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { maska } from 'maska'
import { LoginPayload, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { Options, Vue } from 'vue-class-component'
import { isBizSdAvailable } from '@/utils/bizBrowser/sendToBizBrowser'
import { moveToDetailPageWithScanResult, RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'

@Options({
  components: {
    IconQRcode,
    IconScanQR,
    IconLogout,
    CompanyName,
    DigitCountButton,
    BarcodeSearchButton,
    KeyenceScanButton,
    KeyenceScannerConfig,
    IconHelp,
    RfidScanButton,
    SocketMobileScanComponent,
  },
  directives: { maska },
  name: 'Home',
})
export default class Home extends Vue {
  loading = false
  firstCode = ''
  lastCode = ''
  password = ''
  otpCode = ''
  isDisableCodeInput = true
  error = ''

  created() {
    this.$store.dispatch(CLEAR_BARCODE)
  }

  resetField() {
    this.loading = false
    this.firstCode = ''
    this.lastCode = ''
    this.password = ''
    this.otpCode = ''
    this.isDisableCodeInput = true
    this.error = ''
  }

  get image() {
    return this.$store.getters.customLogoImageUrl || require('@/assets/logo-smartBarcode.png')
  }

  get isQuickTrackingMode() {
    return this.$store.getters.isQuickTrackingModeEnabled
  }

  get isAuth(): boolean {
    return this.$store.state.profile.user !== null
  }

  get currentUserName() {
    const user = this.$store.state.profile.user
    return user?.fullName ?? ''
  }

  get projectParam() {
    return this.$store.getters.projectParam
  }

  get allowGenerateNewBarcode() {
    return this.$store.getters.projectPermission?.generateBarcodePermission?.canEdit || this.isClientUser || false
  }

  get projectDetail() {
    return this.$store.state.project.details
  }

  get showBarcodeSearchPage() {
    return this.projectDetail.showBarcodeSearchPage
  }

  get showTrackingCountPage() {
    return this.projectDetail.showTrackingCountPage
  }

  get isRequirePassword(): boolean {
    return this.projectDetail?.requirePassword
  }

  get isRequireOTP(): boolean {
    return this.projectDetail?.requireOneTimePasscode
  }

  get isAllowUserSelfRegister(): boolean {
    return this.projectDetail?.allowUserSelfRegister
  }

  get loginPayload(): LoginPayload {
    return {
      projectId: this.projectDetail?.id,
      accessCode: `${this.firstCode}${this.lastCode}`,
      password: this.password,
    }
  }

  get nextRouteName() {
    return this.$route?.query?.name ?? ''
  }

  get nextRouteParams() {
    return this.$route.query?.id ?? ''
  }

  get isClientUser(): boolean {
    return this.$store.getters.isClientUser
  }

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  get isBizBrowserEnabled() {
    return isBizSdAvailable()
  }

  onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    const router = this.$router
    moveToDetailPageWithScanResult(rfidList, router)
  }

  onScan(scannedText: string) {
    this.loading = true
    const trimmedScannedText = scannedText.trim()
    const urlParts = trimmedScannedText.split('/')
    const detectedText = trimmedScannedText || ''

    if (trimmedScannedText.match(QRCODE_BARCODE_TYPE_REGEX)) {
      const barcodeId = urlParts.pop()
      urlParts.pop()
      const projectId = urlParts.pop()
      this.$router.push({
        name: 'detail',
        params: { barcodeId, project: projectId },
      })
      return
    }

    const projectCode = this.$route?.params?.project.split('@')
    if (projectCode.length > 1) projectCode.pop()
    this.$router.push({
      name: 'detail',
      params: { barcodeId: `projectCode=${projectCode[0]}` },
      query: { externalId: detectedText, isGenerateBarcode: 1 },
    })
  }

  saveLoginData() {
    this.$store.dispatch(FETCH_PROFILE)
    if (this.nextRouteName && this.nextRouteParams) {
      this.$router.push({
        name: this.nextRouteName,
        params: {
          barcodeId: this.nextRouteParams,
        },
      })
    }
    window.scroll(0, 0)
  }

  async start() {
    try {
      this.loading = true

      const loginPayload = this.loginPayload
      if (!this.isDisableCodeInput) {
        loginPayload.oneTimePasscode = this.otpCode
      }
      await login(this.loginPayload)
      this.resetField()
      this.saveLoginData()
      await this.$store.dispatch(FETCH_PROJECT, this.projectParam)
    } catch (error) {
      if (error) {
        if (typeof error === 'object' && 'oneTimePasscode' in error && typeof error.oneTimePasscode === 'string') {
          if (error?.oneTimePasscode?.includes('1000')) {
            this.isDisableCodeInput = false
            this.error = this.$t('account.confirm_code_already_sent')
            return
          }
          if (error?.oneTimePasscode?.includes('2002')) {
            this.isDisableCodeInput = false
            this.error = this.$t('account.incorrect_otp_passcode')
            return
          }
        }

        switch (error) {
          case '3057':
            openMessage(this.$t('account.confirm_code_sent'), 'success')
            this.isDisableCodeInput = false
            return
          default:
            break
        }
      }

      this.error = this.$t('invalid password')
    } finally {
      this.loading = false
    }
  }

  onCodeInput(value: string) {
    if (value.length === 4) {
      this.$refs.code.focus()
    }
  }

  async onClickGenerate() {
    try {
      this.loading = true
      const result = await generateBarcode(this.$store.getters.projectCode, `${this.$store.getters.projectVersion}`)
      localStorage.setItem(LOCAL_STORAGE_ITEM.CAPTURE_TIMESTAMP, new Date().toISOString())
      this.$router.push({
        name: 'detail',
        params: { project: this.projectParam, barcodeId: result.id },
      })
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

.login-form {
  label {
    margin-bottom: 5px;
  }

  .access-code {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .access-code-input {
      width: 45%;
      display: inline-block;
    }
  }

  .password-input {
    width: 100%;
    margin-top: 15px;
    display: inline-block;
  }

  .go-button {
    margin: 40px auto 0;
  }
}

.logout-button {
  border: 0;
}

.app-links {
  margin: 60px 0px 30px;

  a:link,
  a:hover,
  a:active {
    text-decoration: none;
    color: #778899;
  }

  a:visited {
    text-decoration: none;
    color: #1155dd;
  }
}

.help-block-container {
  position: relative;
  box-sizing: border-box;
  padding: 0rem 0.5rem 0 0.5rem;

  button {
    width: 100%;
  }

  &__child {
    bottom: 0rem;
    right: 0;
    position: absolute;
  }
}
.home-logo {
  width: 100%;
  margin-bottom: 4px;
}
</style>
